import axios from 'shared/lib/axios';
import {
  BulkUpdateConsumablesProductSubscription,
  ConsumablesOrder,
  ConsumablesProduct,
  ConsumablesProductCategory,
  ConsumablesProductSubscription,
  ConsumablesProductType,
  DraftConsumablesOrderItem,
  VendorFee
} from 'shared/types';

export interface ConsumablesPickerDraftPayload {
  selectedByGroup: Record<string, number[]>;
  quantities: Record<string, number>;
  applyMode: 'recurring' | 'one_time';
  viewPhase?: 'picker' | 'pickerExiting' | 'confirmed';
  confirmedSnapshot?: {
    applyMode: 'recurring' | 'one_time';
    lineItems: {
      productId: number;
      title: string;
      quantity: number;
      lineTotalCents: number;
      imageKey: string | null;
    }[];
    totalCents: number;
    /** Open order id used for post-confirm budget verify (web AI picker). */
    budgetCheckOrderId?: number | null;
  } | null;
  confirmDetails?: {
    applyMode: 'recurring' | 'one_time';
    nextOrderLockPhrase: string | null;
    nextOrderEditPath: string | null;
  } | null;
}

export interface ConsumablesPickerDraftResponse {
  draft: {
    id: number;
    ai_assistant_conversation_id: number;
    ai_assistant_message_id: number;
    payload_fingerprint: string;
    state: ConsumablesPickerDraftPayload;
    submitted_at: string | null;
  } | null;
}

export function getConsumablesProducts() {
  return axios.get<ConsumablesProduct[]>('/v1/consumables_products');
}

export function getConsumablesProductDeliveryFees() {
  return axios.get<VendorFee[]>('/v1/consumables_products/delivery_fees');
}

export function getConsumablesProductCategories() {
  return axios.get<ConsumablesProductCategory[]>('/v1/consumables_product_categories');
}

export function getConsumablesProductTypes() {
  return axios.get<ConsumablesProductType[]>('/v1/consumables_product_types');
}

export function getConsumablesOrders() {
  return axios.get<ConsumablesOrder[]>('/v1/consumables_orders');
}

export function getConsumablesProductSubscriptions() {
  return axios.get<ConsumablesProductSubscription[]>('/v1/consumables_product_subscriptions');
}

export function deleteConsumablesProductSubscription(id: number) {
  return axios.delete(`/v1/consumables_product_subscriptions/${id}`);
}

export function subscribeToProduct(consumableServiceProductId: number, quantity: number) {
  return axios.post('/v1/consumables_product_subscriptions/subscribe_to_product', {
    consumables_product_id: consumableServiceProductId,
    quantity: quantity
  });
}

export function updateConsumablesProductSubscription(id: number, quantity: number, consumableServiceProductId: number) {
  return axios.put(`/v1/consumables_product_subscriptions/${id}`, {
    quantity: quantity,
    consumables_product_id: consumableServiceProductId
  });
}

export function deleteConsumablesOrderItem(id: number) {
  return axios.delete(`/v1/consumables_order_items/${id}`);
}

export function createConsumablesOrderItem(consumablesProductId: number, quantity: number) {
  return axios.post('/v1/consumables_order_items', {
    consumables_product_id: consumablesProductId,
    quantity: quantity
  });
}

export function verifyConsumablesOrderBudget(
  orderId: number,
  lines: { product_id: number; quantity: number }[],
  applyMode: 'recurring' | 'one_time'
) {
  return axios.post<{
    valid: boolean;
    error?: string;
    no_budget_configured?: boolean;
    over_budget?: boolean;
    proposed_total?: number;
    max_allowed?: number;
    selection_budget_base_cents?: number | null;
    current_total?: number;
    current_open_order_basket_cents?: number;
  }>('/v1/consumables_order_items/verify_budget', {
    order_id: orderId,
    lines: lines,
    apply_mode: applyMode
  });
}

export function requestConsumablesBudgetSetup() {
  return axios.post<{ success: boolean; message?: string; error?: string }>(
    '/v1/consumables_order_items/request_budget_setup'
  );
}

export function updateConsumablesOrderItem(id: number, quantity: number) {
  return axios.put(`/v1/consumables_order_items/${id}`, { quantity: quantity });
}

export function updateConsumablesProductSubscriptions(
  consumablesProductSubscriptions: BulkUpdateConsumablesProductSubscription[]
) {
  return axios.post('/v1/consumables_product_subscriptions/update_product_subscriptions', {
    consumables_product_subscriptions: consumablesProductSubscriptions
  });
}

export function updateConsumablesOrderItems(orderId: number, orderItems: DraftConsumablesOrderItem[]) {
  return axios.post('/v1/consumables_order_items/update_order_items', {
    orderId: orderId,
    order_items: orderItems
  });
}

export function setUpProductSubscriptions(consumablesProductSubscriptions: BulkUpdateConsumablesProductSubscription[]) {
  return axios.post('/v1/consumables_product_subscriptions/set_up_product_subscriptions', {
    consumables_product_subscriptions: consumablesProductSubscriptions
  });
}

export function createInitialOnboardingOrder() {
  return axios.post('/v1/consumables_orders/create_initial_onboarding_order');
}

export function skipOrder(orderId: number) {
  return axios.post(`/v1/consumables_orders/${orderId}/skip_order`);
}

export function createConsumablesProductSubscription(
  consumablesProductId: number | null | undefined,
  quantity: number
) {
  return axios.post('/v1/consumables_product_subscriptions', {
    consumables_product_id: consumablesProductId,
    quantity: quantity
  });
}

export function getConsumablesPickerDraft(aiAssistantConversationId: number, aiAssistantMessageId: number) {
  return axios.get<ConsumablesPickerDraftResponse>('/v1/consumables_picker_draft', {
    params: {
      ai_assistant_conversation_id: aiAssistantConversationId,
      ai_assistant_message_id: aiAssistantMessageId
    }
  });
}

export function saveConsumablesPickerDraft(
  aiAssistantConversationId: number,
  aiAssistantMessageId: number,
  payloadFingerprint: string,
  state: ConsumablesPickerDraftPayload
) {
  return axios.post<ConsumablesPickerDraftResponse>('/v1/consumables_picker_draft', {
    ai_assistant_conversation_id: aiAssistantConversationId,
    ai_assistant_message_id: aiAssistantMessageId,
    payload_fingerprint: payloadFingerprint,
    state: state
  });
}

export function deleteConsumablesPickerDraft(aiAssistantConversationId: number, aiAssistantMessageId: number) {
  return axios.delete('/v1/consumables_picker_draft', {
    data: {
      ai_assistant_conversation_id: aiAssistantConversationId,
      ai_assistant_message_id: aiAssistantMessageId
    }
  });
}
